// src/.../ISF/ListFileISF.jsx
import React, { useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Cards/Card";
import { ExpandableContent } from "../../../components/CustomControls/ExpandableContent";
import DocumentUploadCard from "../../../components/CustomControls/DocumentUpload/DocumentUploadCard";
import { attachmentEntityType } from "../../../variables/Enums";
import { Add } from "../../../components/Icons/Icons";
import { ModalCreateCategory } from "./ModalCreateCategory";
import { useAuthProtocol, useAuthorization } from "../../../components/Authorization/useAuthorization";
import { toast } from "react-toastify";
import { FaAsterisk, FaDownload } from "react-icons/fa";
import { markRequiredBatch, downloadAttachmentsZip } from "../../../api/clinicalTrial";

const CATEGORY_KEYS = ["startup", "protocol", "practitioners", "regulatory", "hospital"];

const getAttachmentsForCategory = (isfFiles, key) => {
  if (!isfFiles) return [];
  switch (key) {
    case "startup":
      return isfFiles.startupAttachments || [];
    case "protocol":
      return isfFiles.protocolAttachments || [];
    case "practitioners":
      return (isfFiles.practitioners || []).flatMap((p) => p.attachments || []);
    case "regulatory":
      return (isfFiles.regulatorySubmissions || []).flatMap((rs) => rs.attachments || []);
    case "hospital":
      return isfFiles.hospitalAttachments || [];
    default:
      return [];
  }
};

const categoryHasFiles = (isfFiles, key) => {
  return getAttachmentsForCategory(isfFiles, key).some((a) => !!a?.id);
};

const categoryHasRequired = (isfFiles, key) => {
  return getAttachmentsForCategory(isfFiles, key).some((a) => a?.required === true);
};

const categoryHasNotRequired = (isfFiles, key) => {
  return getAttachmentsForCategory(isfFiles, key).some((a) => a?.required !== true);
};

const shouldShowCategory = (isfFiles, key, categoryFilter) => {
  if (!categoryFilter || categoryFilter === "all") return true;
  if (categoryFilter === "withFiles") return categoryHasFiles(isfFiles, key);
  if (categoryFilter === "withoutFiles") return !categoryHasFiles(isfFiles, key);
  if (categoryFilter === "required") return categoryHasRequired(isfFiles, key);
  if (categoryFilter === "notRequired") return categoryHasNotRequired(isfFiles, key);
  return true;
};

const filterAttachmentsByCategoryFilter = (attachments, categoryFilter) => {
  if (!attachments || !categoryFilter || categoryFilter === "all" ||
      categoryFilter === "withFiles" || categoryFilter === "withoutFiles") {
    return attachments || [];
  }
  if (categoryFilter === "required") {
    return (attachments || []).filter((a) => a?.required === true);
  }
  if (categoryFilter === "notRequired") {
    return (attachments || []).filter((a) => a?.required !== true);
  }
  return attachments || [];
};

export default function ListFileISF({
  isfFiles,
  groupArrayByTwo,
  handleCategoryNameChange,
  showNewCategoryForm,
  setShowNewCategoryForm,
  handleCategoryCreated,
  onAttachmentChanged,
  protocolId,
  categoryFilter,
}) {
  const { t } = useTranslation();
  const [, , isAuthorized] = useAuthorization();
  const [isExternalNotesAuthorized, isAuthLoading] = useAuthProtocol(protocolId);
  const [isRequiringAll, setIsRequiringAll] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const slotsToRequire = useMemo(() => {
    if (!isfFiles || categoryFilter !== "withoutFiles") return [];

    const typeByKey = {
      startup: attachmentEntityType.Startup,
      protocol: attachmentEntityType.Protocols,
      practitioners: attachmentEntityType.Practitioner,
      regulatory: attachmentEntityType.RegulatorySubmissions,
      hospital: attachmentEntityType.Hospital,
    };

    return CATEGORY_KEYS.flatMap((key) => {
      if (!shouldShowCategory(isfFiles, key, categoryFilter)) return [];

      const attachments = filterAttachmentsByCategoryFilter(
        getAttachmentsForCategory(isfFiles, key),
        categoryFilter
      );

      const typeId = typeByKey[key];
      if (!typeId) return [];

      return (attachments || []).map((item) => ({
        typeId,
        categoryId: item?.attachmentCategoryId,
        entityId: key === "hospital" ? item?.entityId ?? "global" : item?.entityId,
        attachmentId: item?.id ?? null,
      }));
    });
  }, [isfFiles, categoryFilter]);

  const slotsToDownload = useMemo(() => {
    if (!isfFiles) return [];
    if (categoryFilter !== "all" && categoryFilter !== "withFiles") return [];

    const typeByKey = {
      startup: attachmentEntityType.Startup,
      protocol: attachmentEntityType.Protocols,
      practitioners: attachmentEntityType.Practitioner,
      regulatory: attachmentEntityType.RegulatorySubmissions,
      hospital: attachmentEntityType.Hospital,
    };

    const folderByKey = {
      startup: t("clinicalTrials.isfFiles.startupFiles") || "Archivos de Startup",
      protocol: t("clinicalTrials.isfFiles.protocolFiles") || "Archivos de Protocolo",
      practitioners: t("clinicalTrials.isfFiles.practitionerFiles") || "Archivos de Investigadores",
      regulatory: t("clinicalTrials.isfFiles.regulatoryFiles") || "Archivos Regulatorios",
      hospital: t("clinicalTrials.isfFiles.hospitalFiles") || "Archivos de Hospital",
    };

    return CATEGORY_KEYS.flatMap((key) => {
      if (!shouldShowCategory(isfFiles, key, categoryFilter)) return [];

      const attachments = filterAttachmentsByCategoryFilter(
        getAttachmentsForCategory(isfFiles, key),
        categoryFilter
      );

      const typeId = typeByKey[key];
      if (!typeId) return [];

      return (attachments || [])
        .filter((item) => item?.id != null && item.id > 0)
        .map((item) => ({
          typeId,
          attachmentId: item.id,
          folderName: folderByKey[key],
        }));
    });
  }, [isfFiles, categoryFilter, t]);

  const handleDownloadAll = async () => {
    if (!slotsToDownload || slotsToDownload.length === 0) return;

    setIsDownloading(true);
    try {
      await downloadAttachmentsZip(slotsToDownload);
    } catch (err) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        "Error al descargar archivos.";
      toast.error(msg);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRequireAllWithoutFiles = async () => {
    if (!slotsToRequire || slotsToRequire.length === 0) return;

    setIsRequiringAll(true);
    try {
      await markRequiredBatch(slotsToRequire);
      toast.success(t("attachments.markedRequired") || "Requeridos");
    } catch (err) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        "Error al requerir documentos.";
      toast.error(msg);
    } finally {
      setIsRequiringAll(false);
    }
  };

  if (!isfFiles) return null;

  const visibleCategories = CATEGORY_KEYS.filter((k) =>
    shouldShowCategory(isfFiles, k, categoryFilter)
  );

  if (categoryFilter && categoryFilter !== "all" && visibleCategories.length === 0) {
    return (
      <div className="text-muted text-center py-4">
        {t("clinicalTrials.isfFiles.noCategoriesMatch") || "No hay categorías que coincidan con el filtro."}
      </div>
    );
  }

  return (
    <Card
      content={
        <>
          {(categoryFilter === "withoutFiles" ||
            categoryFilter === "all" ||
            categoryFilter === "withFiles") && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 12,
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {(categoryFilter === "all" || categoryFilter === "withFiles") && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleDownloadAll}
                  disabled={isDownloading || slotsToDownload.length === 0}
                >
                  <FaDownload style={{ marginRight: 6 }} />
                  {isDownloading ? "Descargando..." : "Descargar archivos"}
                </button>
              )}
              {categoryFilter === "withoutFiles" && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleRequireAllWithoutFiles}
                  disabled={
                    !isExternalNotesAuthorized ||
                    isAuthLoading ||
                    isRequiringAll ||
                    slotsToRequire.length === 0
                  }
                >
                  <FaAsterisk style={{ marginRight: 6 }} />
                  Requerir todos
                </button>
              )}
            </div>
          )}

          {/* STARTUP */}
          {shouldShowCategory(isfFiles, "startup", categoryFilter) && (
          <ExpandableContent title={t("clinicalTrials.isfFiles.startupFiles")}>
            {(() => {
              const attachments = filterAttachmentsByCategoryFilter(
                isfFiles.startupAttachments,
                categoryFilter
              );
              return !attachments || attachments.length === 0 ? (
                <div className="text-muted text-center py-3">
                  {t("clinicalTrials.isfFiles.emptyCategoryMessage")}
                </div>
              ) : (
              groupArrayByTwo(attachments).map((group, idx) => (
                <Row key={idx}>
                  {group.map((item) => (
                    <Col md={6} key={item.attachmentCategoryId}>
                      <DocumentUploadCard
                        attachmentEntityTypeId={attachmentEntityType.Startup}
                        entityId={item.entityId}
                        protocolId={protocolId}
                        attachment={item}
                        legend="Subir documento"
                        editCategoryName={true}
                        onCategoryNameChange={handleCategoryNameChange}
                        onAttachmentChange={(attachment) =>
                          onAttachmentChanged(
                            attachment.entityTypeId,
                            item.entityId,
                            attachment
                          )
                        }
                        disabled={!isAuthorized(["Startups.Edit"])}
                      />
                    </Col>
                  ))}
                </Row>
              ))
            );
            })()}
          </ExpandableContent>
          )}

          {/* PROTOCOL */}
          {shouldShowCategory(isfFiles, "protocol", categoryFilter) && (
          <ExpandableContent title={t("clinicalTrials.isfFiles.protocolFiles")}>
            {(() => {
              const attachments = filterAttachmentsByCategoryFilter(
                isfFiles.protocolAttachments,
                categoryFilter
              );
              return !attachments || attachments.length === 0 ? (
                <div className="text-muted text-center py-3">
                  {t("clinicalTrials.isfFiles.emptyCategoryMessage")}
                </div>
              ) : (
              groupArrayByTwo(attachments).map(
                (group, idx) => (
                  <Row key={idx}>
                    {/* Evito modificaciones en archivo de protocolo. Solo se puede modificar desde el protocolo */}
                    {group.map((item) => (
                      <Col md={6} key={item.attachmentCategoryId}>
                        <DocumentUploadCard
                          attachmentEntityTypeId={
                            attachmentEntityType.Protocols
                          }
                          entityId={item.entityId}
                          protocolId={protocolId}
                          attachment={item}
                          disabled
                          showDownloadButton={false}
                        />
                      </Col>
                    ))}
                  </Row>
                )
              )
            );
            })()}
          </ExpandableContent>
          )}

          {/* PRACTITIONERS */}
          {shouldShowCategory(isfFiles, "practitioners", categoryFilter) && (
          <ExpandableContent
            title={t("clinicalTrials.isfFiles.practitionerFiles")}
          >
            {!isfFiles.practitioners || isfFiles.practitioners.length === 0 ? (
              <div className="text-muted text-center py-3">
                {t("clinicalTrials.isfFiles.emptyCategoryMessage")}
              </div>
            ) : (
              isfFiles.practitioners.map((practitioner) => (
                <div key={practitioner.id}>
                  <Row>
                    <Col md={3}>
                      <strong style={{ display: "block", marginBottom: "4px" }}>
                        {t("clinicalTrials.isfFiles.name")}
                      </strong>
                      <div className="text-muted mb-2">{practitioner.name}</div>
                    </Col>
                    <Col md={3}>
                      <strong style={{ display: "block", marginBottom: "4px" }}>
                        {t("clinicalTrials.isfFiles.studyRole")}
                      </strong>
                      <div className="text-muted mb-2">
                        {practitioner.studyRoleName}
                      </div>
                    </Col>
                  </Row>
                  {(() => {
                    const attachments = filterAttachmentsByCategoryFilter(
                      practitioner.attachments,
                      categoryFilter
                    );
                    return !attachments || attachments.length === 0 ? (
                    <div className="text-muted text-center py-2 mb-3">
                      {t("clinicalTrials.isfFiles.emptyCategoryMessage")}
                    </div>
                  ) : (
                    groupArrayByTwo(attachments).map(
                      (group, idx) => (
                        <Row key={idx}>
                          {group.map((item) => (
                            <Col md={6} key={item.attachmentCategoryId}>
                              <DocumentUploadCard
                                attachmentEntityTypeId={
                                  attachmentEntityType.Practitioner
                                }
                                entityId={item.entityId}
                                protocolId={protocolId}
                                attachment={item}
                                legend="Subir documento"
                                showDownloadButton={false}
                                editCategoryName={true}
                                onCategoryNameChange={handleCategoryNameChange}
                                onAttachmentChange={(attachment) =>
                                  onAttachmentChanged(
                                    attachment.entityTypeId,
                                    item.entityId,
                                    attachment
                                  )
                                }
                              />
                            </Col>
                          ))}
                        </Row>
                      )
                    )
                  );
                  })()}
                  <hr />
                </div>
              ))
            )}
          </ExpandableContent>
          )}

          {/* REGULATORY */}
          {shouldShowCategory(isfFiles, "regulatory", categoryFilter) && (
          <ExpandableContent
            title={t("clinicalTrials.isfFiles.regulatoryFiles")}
          >
            {!isfFiles.regulatorySubmissions ||
            isfFiles.regulatorySubmissions.length === 0 ? (
              <div className="text-muted text-center py-3">
                {t("clinicalTrials.isfFiles.emptyCategoryMessage")}
              </div>
            ) : (
              isfFiles.regulatorySubmissions.map((submission) => (
                <div key={submission.id}>
                  <Row>
                    <Col md={3}>
                      <strong style={{ display: "block", marginBottom: "4px" }}>
                        {t("clinicalTrials.isfFiles.regulatorySubmission")}
                      </strong>
                      <div className="text-muted mb-2">{submission.name}</div>
                    </Col>
                  </Row>
                  {(() => {
                    const attachments = filterAttachmentsByCategoryFilter(
                      submission.attachments || [],
                      categoryFilter
                    );
                    return !attachments || attachments.length === 0 ? (
                    <div className="text-muted text-center py-2 mb-3">
                      {t("clinicalTrials.isfFiles.emptyCategoryMessage")}
                    </div>
                  ) : (
                    groupArrayByTwo(attachments).map(
                      (group, idx) => (
                        <Row key={idx}>
                          {group.map((item) => (
                            <Col md={6} key={item.attachmentCategoryId}>
                              <DocumentUploadCard
                                attachmentEntityTypeId={
                                  attachmentEntityType.RegulatorySubmissions
                                }
                                entityId={item.entityId}
                                protocolId={protocolId}
                                attachment={item}
                                legend="Subir documento"
                                showDownloadButton={false}
                                editCategoryName={true}
                                onCategoryNameChange={handleCategoryNameChange}
                                onAttachmentChange={(attachment) =>
                                  onAttachmentChanged(
                                    attachment.entityTypeId,
                                    item.entityId,
                                    attachment
                                  )
                                }
                              />
                            </Col>
                          ))}
                        </Row>
                      )
                    )
                  );
                  })()}
                  <hr />
                </div>
              ))
            )}
          </ExpandableContent>
          )}

          {/* HOSPITAL */}
          {shouldShowCategory(isfFiles, "hospital", categoryFilter) && (
          <ExpandableContent title={t("clinicalTrials.isfFiles.hospitalFiles")}>
            {(() => {
              const attachments = filterAttachmentsByCategoryFilter(
                isfFiles.hospitalAttachments || [],
                categoryFilter
              );
              return !attachments || attachments.length === 0 ? (
                <div className="text-muted text-center py-3">
                  {t("clinicalTrials.isfFiles.emptyCategoryMessage")}
                </div>
              ) : (
              groupArrayByTwo(attachments).map(
                (group, idx) => (
                  <Row key={idx}>
                    {group.map((item) => (
                      <Col md={6} key={item.attachmentCategoryId}>
                        {item.deviceName && (
                          <div
                            style={{
                              marginBottom: "8px",
                              fontWeight: 600,
                              color: "#495057",
                              fontSize: "14px",
                            }}
                          >
                            {item.deviceName}
                          </div>
                        )}
                        <DocumentUploadCard
                          attachmentEntityTypeId={attachmentEntityType.Hospital}
                          entityId={item.entityId ?? "global"}
                          protocolId={protocolId}
                          attachment={item}
                          legend="Subir documento"
                          showDownloadButton={false}
                          editCategoryName={true}
                          onCategoryNameChange={handleCategoryNameChange}
                          onAttachmentChange={(attachment) =>
                            onAttachmentChanged(
                              attachment.entityTypeId,
                              item.entityId ?? "global",
                              attachment
                            )
                          }
                        />
                      </Col>
                    ))}
                  </Row>
                )
              )
            );
            })()}
          </ExpandableContent>
          )}

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowNewCategoryForm(true)}
            disabled={!isAuthorized(["Startups.Edit"])}
          >
            <Add />
            {t("clinicalTrials.isfFiles.createCategory")}
          </button>

          <ModalCreateCategory
            show={showNewCategoryForm}
            onClose={() => setShowNewCategoryForm(false)}
            onCreated={handleCategoryCreated}
          />
        </>
      }
    />
  );
}
